const STORAGE_KEY = 'lwr-referral-attribution-v1';
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;

// No identifying family data is returned by the API or stored in the browser.
export function createReferralAttribution({ storage, fetcher, now = Date.now } = {}) {
  let attribution = null;
  let remembered = false;
  let notice = '';
  let requestId = 0;
  let pending = Promise.resolve();
  let inFlightCode = null;
  const listeners = new Set();
  const removeStored = () => { try { storage?.removeItem(STORAGE_KEY); } catch { /* Storage may be disabled. */ } };
  try {
    const saved = JSON.parse(storage?.getItem(STORAGE_KEY) || 'null');
    if (saved?.consent === true && typeof saved.token === 'string' && saved.expiresAt > now() && saved.expiresAt <= now() + MAX_AGE) {
      attribution = { token: saved.token, expiresAt: saved.expiresAt };
      remembered = true;
    } else if (saved) removeStored();
  } catch { removeStored(); }
  const publish = () => listeners.forEach(listener => listener());
  const save = () => {
    if (!attribution || !remembered) return;
    try {
      if (!storage) throw new Error('Storage unavailable');
      storage.setItem(STORAGE_KEY, JSON.stringify({ ...attribution, consent: true }));
    }
    catch { remembered = false; notice = 'Your browser could not remember the referral. It is still available for this visit.'; }
  };
  const expire = () => {
    if (attribution && attribution.expiresAt <= now()) {
      attribution = null; remembered = false; removeStored();
    }
  };
  function capture(search) {
    const code = new URLSearchParams(search).get('ref');
    expire();
    if (code === null) return pending;
    if (inFlightCode === code) return pending;
    const id = ++requestId;
    inFlightCode = code;
    pending = (async () => {
      const controller = typeof AbortController === 'undefined' ? null : new AbortController();
      const timeout = controller ? setTimeout(() => controller.abort(), 8000) : null;
      try {
        const response = await fetcher('/api/referrals/validate', { method: 'POST',
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }), cache: 'no-store',
          ...(controller ? { signal: controller.signal } : {}) });
        if (!response.ok) throw new Error('Validation unavailable');
        const data = await response.json();
        if (id !== requestId) return;
        if (data.valid && typeof data.token === 'string' && data.expiresAt > now() && data.expiresAt <= now() + MAX_AGE) {
          attribution = { token: data.token, expiresAt: data.expiresAt };
          notice = '';
          save();
        }
        // Invalid or disabled links never replace a previous valid referral.
      } catch {
        if (id === requestId) notice = 'We could not check this referral link. You can still enquire and optionally tell us who referred you.';
      } finally {
        if (timeout) clearTimeout(timeout);
        if (id === requestId) { inFlightCode = null; publish(); }
      }
    })();
    return pending;
  }
  return {
    capture,
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); },
    snapshot() { expire(); return { hasReferral: !!attribution, remembered, notice }; },
    remember() { expire(); remembered = !!attribution; save(); publish(); },
    forget() { ++requestId; inFlightCode = null; attribution = null; remembered = false; notice = ''; removeStored(); publish(); },
    dismissNotice() { notice = ''; publish(); },
    async submission() { await pending; expire(); return attribution?.token || null; }
  };
}

// With no referral consent prompt, attribution stays in memory for this visit.
try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be unavailable. */ }
export const referralAttribution = createReferralAttribution({
  storage: null,
  fetcher: (...args) => window.fetch(...args)
});
